import { FetchError } from '../errors/FetchError'
import { readJsonResponse } from '../errors/tooManyRequests'
import { APP_SETTINGS } from '../utils'
import { fetchApiImageAuth3, fetchFileWithAuthorization, fetchWithAuthorization } from './fetch'

const appendValue = (formData, key, value) => {
  if (value === undefined || value === null) return
  if (Array.isArray(value)) {
    value.forEach((item) => appendValue(formData, `${key}[]`, item))
    return
  }
  formData.append(key, value)
}

export const toFormData = (payload) => {
  const formData = new FormData()
  Object.entries(payload).forEach(([key, value]) => {
    if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof File)) {
      Object.entries(value).forEach(([childKey, childValue]) => appendValue(formData, `${key}[${childKey}]`, childValue))
      return
    }
    appendValue(formData, key, value)
  })
  return formData
}

export const registerProvider = async (payload) => {
  const response = await fetch(`${APP_SETTINGS.API_URL}/marketplace/providers/register`, {
    method: 'POST',
    headers: {
      Accept: 'application/json, text/plain, */*',
      'X-Requested-With': 'XMLHttpRequest'
    },
    body: toFormData(payload)
  })
  const data = await readJsonResponse(response)

  if (!response.ok) {
    throw new FetchError(data, 'No pudimos completar tu registro.')
  }

  return data
}

export const getMarketplaceCategories = async () => {
  try {
    return await fetchWithAuthorization('/marketplace/categories')
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar las categorías.')
  }
}

export const getMyProviderProfile = async () => {
  try {
    return await fetchWithAuthorization('/marketplace/provider/me')
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar tu perfil de proveedor.')
  }
}

export const updateProviderDocuments = async (documents) => {
  try {
    return await fetchApiImageAuth3('/marketplace/provider/me/documents', toFormData({ documents }), 'POST')
  } catch (error) {
    throw new FetchError(error, 'No pudimos subir tus documentos.')
  }
}

export const getMyServices = async () => {
  try {
    return await fetchWithAuthorization('/marketplace/provider/services')
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar tus servicios.')
  }
}

export const getMyService = async (id) => {
  try {
    return await fetchWithAuthorization(`/marketplace/provider/services/${id}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar el servicio.')
  }
}

export const createService = async (payload) => {
  try {
    return await fetchApiImageAuth3('/marketplace/provider/services', toFormData(payload), 'POST')
  } catch (error) {
    throw new FetchError(error, 'No pudimos publicar el servicio.')
  }
}

export const updateService = async (id, payload) => {
  try {
    return await fetchApiImageAuth3(
      `/marketplace/provider/services/${id}`,
      toFormData({ ...payload, _method: 'PUT' }),
      'POST'
    )
  } catch (error) {
    throw new FetchError(error, 'No pudimos guardar los cambios del servicio.')
  }
}

export const updateServiceStatus = async (id, status) => {
  try {
    return await fetchWithAuthorization(`/marketplace/provider/services/${id}/status`, { status }, 'PUT')
  } catch (error) {
    throw new FetchError(error, 'No pudimos cambiar el estado del servicio.')
  }
}

export const deleteService = async (id) => {
  try {
    return await fetchWithAuthorization(`/marketplace/provider/services/${id}`, null, 'DELETE')
  } catch (error) {
    throw new FetchError(error, 'No pudimos eliminar el servicio.')
  }
}

export const getMarketplaceServices = async (query = '') => {
  try {
    return await fetchWithAuthorization(`/marketplace/services${query ? `?${query}` : ''}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar los servicios del Marketplace.')
  }
}

export const getMarketplaceService = async (id) => {
  try {
    return await fetchWithAuthorization(`/marketplace/services/${id}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar el servicio.')
  }
}

export const getFeaturedProviders = async () => {
  try {
    return await fetchWithAuthorization('/marketplace/providers/featured')
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar los proveedores destacados.')
  }
}

export const getServiceRequests = async (query = '') => {
  try {
    return await fetchWithAuthorization(`/marketplace/requests${query ? `?${query}` : ''}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar las solicitudes.')
  }
}

export const getServiceRequest = async (id) => {
  try {
    return await fetchWithAuthorization(`/marketplace/requests/${id}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar la conversación.')
  }
}

export const serviceRequestAction = async (id, action, payload = {}) => {
  try {
    return await fetchWithAuthorization(`/marketplace/requests/${id}/${action}`, payload, 'POST')
  } catch (error) {
    throw new FetchError(error, 'No pudimos completar la acción.')
  }
}

export const getAdminProviders = async (query = '') => {
  try {
    return await fetchWithAuthorization(`/marketplace/admin/providers${query ? `?${query}` : ''}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar los proveedores.')
  }
}

export const getAdminProvider = async (id) => {
  try {
    return await fetchWithAuthorization(`/marketplace/admin/providers/${id}`)
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar el proveedor.')
  }
}

export const getAdminProviderDocument = (providerId, documentId) =>
  fetchFileWithAuthorization(`/marketplace/admin/providers/${providerId}/documents/${documentId}`)

export const reviewProvider = async (id, action, payload = {}) => {
  try {
    return await fetchWithAuthorization(`/marketplace/admin/providers/${id}/${action}`, payload, 'POST')
  } catch (error) {
    throw new FetchError(error, 'No pudimos guardar la revisión.')
  }
}

export const getAdminCategories = async () => {
  try {
    return await fetchWithAuthorization('/marketplace/admin/categories')
  } catch (error) {
    throw new FetchError(error, 'No pudimos cargar las categorías.')
  }
}

export const saveAdminCategory = async (category) => {
  try {
    return category.id
      ? await fetchWithAuthorization(`/marketplace/admin/categories/${category.id}`, category, 'PUT')
      : await fetchWithAuthorization('/marketplace/admin/categories', category, 'POST')
  } catch (error) {
    throw new FetchError(error, 'No pudimos guardar la categoría.')
  }
}

export const reorderAdminCategories = async (ids) => {
  try {
    return await fetchWithAuthorization('/marketplace/admin/categories/order', { ids }, 'PUT')
  } catch (error) {
    throw new FetchError(error, 'No pudimos guardar el orden.')
  }
}

export const createServiceRequest = async (payload) => {
  try {
    return await fetchApiImageAuth3('/marketplace/requests', toFormData(payload), 'POST')
  } catch (error) {
    throw new FetchError(error, 'No pudimos enviar tu solicitud.')
  }
}
